import { useEffect, useId, useRef } from 'react';
import {
  DELIVERY_TEXT, MAX_PHONES, PHONE_HINT_TEXT, PHONE_TYPES, PHONE_TYPE_LABEL, addRow, deliveryFor,
  isCallable, isEdited, makePrimary, phoneHint, primaryIndex, printedHint, removeRow, updateRow,
  type PhoneDeliveryLine, type PhoneRow, type PhoneType,
} from './phoneList.js';

/**
 * Every phone number a contact has, each with its type, in the person's order.
 *
 * Replaces the single Phone field, which kept the first number on a card and silently
 * dropped the rest — the mobile on a card that printed the office line first never
 * reached anyone. The rules live in phoneList.ts; this is only their presentation.
 *
 * One row per number: a type, the number, and Remove. The first callable number is the
 * primary and says so; any other callable number can be made primary, which moves it to
 * the top. A fax says it cannot be called. Below a number: the one question about it, if
 * any (it can't be read; it may be a fax; it was hard to read) — computed from the row as
 * it is now, so it disappears once answered — what the server says will happen to it
 * (only when it will not simply be sent), the label as printed when it differs from the
 * type, or that it was changed here.
 */

const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900';
const CONTROL = `min-h-11 min-w-0 rounded-md border bg-white px-2 text-base sm:text-sm disabled:bg-slate-50 disabled:text-slate-500 ${FOCUS}`;

export function PhoneListEditor({
  rows, onChange, deliveries, error, disabled = false, legend = 'Phone numbers',
}: {
  rows: readonly PhoneRow[];
  onChange: (rows: PhoneRow[]) => void;
  /** The server's word on each number, from the last check. */
  deliveries?: readonly PhoneDeliveryLine[];
  /** A 400 invalid_phone from the last save: its message, and which values it refused. */
  error?: { message: string; values: readonly string[] } | null;
  disabled?: boolean;
  legend?: string;
}) {
  const base = useId();
  const primary = primaryIndex(rows);
  const errorId = `${base}-error`;
  const refused = new Set((error?.values ?? []).map((v) => v.trim()));
  const errorRef = useRef<HTMLParagraphElement | null>(null);
  // A refusal arrives after pressing a button at the bottom; bring the numbers into view.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ block: 'center' });
  }, [error]);

  return (
    <fieldset className="min-w-0" data-phone-editor="" disabled={disabled}>
      <legend className="text-sm font-medium text-slate-700">{legend}</legend>
      {error ? (
        <p ref={errorRef} id={errorId} role="alert" className="mt-1 text-sm text-red-700" data-phone-error="">
          {error.message}
        </p>
      ) : null}
      {rows.length === 0 ? (
        <p className="mt-1 text-sm text-slate-500">No phone numbers.</p>
      ) : null}
      <ol className="mt-2 min-w-0 space-y-3">
        {rows.map((r, i) => {
          const id = `${base}-${r.key}`;
          const bad = refused.has(r.value.trim());
          const delivery = deliveryFor(r, deliveries);
          const printed = printedHint(r);
          const hint = phoneHint(r);
          const uncertain = hint !== null;
          const deliveryNote = delivery && delivery !== 'will_send' ? DELIVERY_TEXT[delivery] : null;
          const hintId = `${id}-hint`;
          return (
            <li key={r.key} className="min-w-0" data-phone-row={r.type}>
              <div className="grid min-w-0 grid-cols-[minmax(6.5rem,8rem)_minmax(0,1fr)] gap-2">
                <label className="sr-only" htmlFor={`${id}-type`}>Type of phone {i + 1}</label>
                <select
                  id={`${id}-type`}
                  value={r.type}
                  onChange={(e) => onChange(updateRow(rows, r.key, { type: e.target.value as PhoneType }))}
                  className={`${CONTROL} border-slate-300`}
                >
                  {PHONE_TYPES.map((t) => <option key={t} value={t}>{PHONE_TYPE_LABEL[t]}</option>)}
                </select>
                <label className="sr-only" htmlFor={`${id}-value`}>Phone {i + 1}</label>
                <input
                  id={`${id}-value`}
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  value={r.value}
                  aria-invalid={bad || hint === 'invalid' ? 'true' : undefined}
                  aria-describedby={[bad ? errorId : '', hint ? hintId : ''].filter(Boolean).join(' ') || undefined}
                  onChange={(e) => onChange(updateRow(rows, r.key, { value: e.target.value }))}
                  className={`${CONTROL} w-full px-3 ${
                    bad ? 'border-red-500 bg-red-50/40' : uncertain ? 'border-amber-300 bg-amber-50/40' : 'border-slate-300'
                  }`}
                />
              </div>
              {hint ? (
                <p id={hintId} className="mt-1 min-w-0 text-xs font-medium text-amber-800 [overflow-wrap:anywhere]" data-phone-hint={hint}>
                  {PHONE_HINT_TEXT[hint]}
                </p>
              ) : null}
              {deliveryNote || printed ? (
                <div className="mt-1 min-w-0 space-y-0.5 text-xs">
                  {deliveryNote ? (
                    <p className="min-w-0 text-slate-600 [overflow-wrap:anywhere]" data-phone-delivery-note="">{deliveryNote}</p>
                  ) : null}
                  {printed ? <p className="min-w-0 text-slate-500 [overflow-wrap:anywhere]" title={printed}>{printed}</p> : null}
                </div>
              ) : null}
              <div className="flex min-w-0 items-center gap-x-3 text-xs">
                {i === primary ? (
                  <span className="rounded bg-slate-900 px-1.5 py-0.5 font-medium text-white" data-phone-primary="">Primary</span>
                ) : isCallable(r.type) && r.value.trim() !== '' ? (
                  <button
                    type="button"
                    onClick={() => onChange(makePrimary(rows, r.key))}
                    className={`inline-flex min-h-11 shrink-0 items-center rounded px-1 text-slate-700 underline ${FOCUS}`}
                  >
                    Make primary
                  </button>
                ) : null}
                {!isCallable(r.type) ? <span className="font-medium text-slate-600">Fax — not callable</span> : null}
                {!deliveryNote && !hint && isEdited(r) && r.original !== undefined ? <span className="text-slate-400">Edited</span> : null}
                <button
                  type="button"
                  onClick={() => onChange(removeRow(rows, r.key))}
                  aria-label={`Remove phone ${r.value.trim() || i + 1}`}
                  className={`ml-auto inline-flex min-h-11 shrink-0 items-center rounded px-1 text-slate-600 underline ${FOCUS}`}
                >
                  Remove
                </button>
              </div>
            </li>
          );
        })}
      </ol>
      {rows.length < MAX_PHONES ? (
        <button
          type="button"
          onClick={() => onChange(addRow(rows))}
          className={`mt-2 inline-flex min-h-11 items-center rounded px-1 text-sm font-medium text-slate-800 underline ${FOCUS}`}
        >
          + Add phone
        </button>
      ) : null}
    </fieldset>
  );
}

/**
 * The numbers, read-only, each with its type: an email capture's people when nobody is
 * editing them. Says per number what will not simply be sent, like the editor.
 */
export function PhoneList({ phones, deliveries }: {
  phones: ReadonlyArray<{ value: string; type?: string | null }>;
  deliveries?: readonly PhoneDeliveryLine[];
}) {
  if (phones.length === 0) return null;
  const typed = phones.map((p) => {
    const t = (PHONE_TYPES as readonly string[]).includes(p.type ?? '') ? p.type as PhoneType : 'unknown';
    return { ...p, t, d: deliveryFor({ key: p.value, value: p.value, type: t, e164: p.value }, deliveries) };
  });
  // When every number shares the same fate, say it once rather than under each.
  const first = typed[0]!.d;
  const shared = first && first !== 'will_send' && typed.every((p) => p.d === first) ? first : null;
  return (
    <div className="min-w-0">
      <ul className="min-w-0 space-y-0.5" aria-label="Phone numbers">
        {typed.map((p) => (
          <li key={`${p.t}-${p.value}`} className="min-w-0 [overflow-wrap:anywhere]">
            <span className="text-slate-500">{PHONE_TYPE_LABEL[p.t]}</span>{' '}
            <span className="text-slate-800">{p.value}</span>
            {p.t === 'fax' ? <span className="text-slate-500"> · not callable</span> : null}
            {!shared && p.d && p.d !== 'will_send' ? <span className="block text-slate-500">{DELIVERY_TEXT[p.d]}</span> : null}
          </li>
        ))}
      </ul>
      {shared ? <p className="mt-0.5 text-slate-500">{DELIVERY_TEXT[shared]}</p> : null}
    </div>
  );
}
